import { useShoppingCart } from "@/src/context/shopping-cart/shopping-cart-context"
import { useMessage } from "@/src/context/message/message-context"
import { Overlay } from "@/src/components/helpers/overlays/overlay"
import { useCurrentProduct } from "@/src/context/product/current-product-context"
import ItemInfos from "@/src/interfaces/catalog/item-infos"
import ShoppingCart from "@/src/interfaces/system/shopping-cart"
import "@/src/extensions/string"
import { useGoogleAnalytics } from "@/src/context/google-analytics/google-analytics-context"
import { useProductValidations } from "../product/useValidations"
import { ProductTypeId } from "../product/useWizard"
import ShoppingCartFactory, { FactoryParams } from "./functions"
import { useMerchant } from "@/src/context/merchant/merchant-context"

export const useAddItem = () => {
    const { addProduct } = useShoppingCart()
    const { validateWizard } = useProductValidations()
    const { observation, price, answersFromCustomer, sizeProduct, currentProduct } = useCurrentProduct()
    const { merchantSummary } = useMerchant()
    const { showMessage } = useMessage()
    const { addToCartGA } = useGoogleAnalytics()

    const handleAddItem = (cartItem: ShoppingCart.CartItem) => {
        addProduct(cartItem)
        addToCartGA({
            currency: 'BRL',
            value: cartItem.price,
            items: [
                {
                    item_id: cartItem.id,
                    item_name: cartItem.name
                }
            ]
        })
    }

    const addToCart = (productDetails: ItemInfos.DetailedResponse, quantity: number) => {
        if (!productDetails) return showMessage("Não foi possível adicionar o produto.")

        const isRedeemItem = !!productDetails.loyaltyCampaignId

        if (!isRedeemItem && validateWizard().hasError)
            return

        const totalPrice = (price * quantity).customRound(4).roundAwayFromZero(2)
        const redeemObs = isRedeemItem ? ' (ganhou)' : ''
        const isCombo = productDetails.itemTypeId === ProductTypeId.Combo
        const hasAnswersFromCustomer = !isRedeemItem && (answersFromCustomer && answersFromCustomer.length > 0)

        let orderedAnswers = ShoppingCartFactory.getOrderedAnswers(answersFromCustomer, currentProduct)
        const hasWizard = orderedAnswers?.length > 0

        const params: FactoryParams = {
            quantity: quantity,
            redeemObs: redeemObs,
            productDetails: productDetails,
            price: price,
            hasWizard: hasWizard,
            totalPrice: totalPrice,
            isCombo: isCombo,
            answersFromCustomer: answersFromCustomer
        }

        // Dado inicial do carrinho.
        let cartItem: ShoppingCart.CartItem = {
            id: productDetails.id,
            price: productDetails.salePrice,
            quantity: quantity,
            externalId: productDetails.productDetailsExternalId,
            name: ShoppingCartFactory.getName(productDetails.name, redeemObs),
            totalPrice: totalPrice,
            type: ShoppingCartFactory.getMainItemType(answersFromCustomer),
            isGroup: false,
            subItems: [] as ShoppingCart.CartItem[],
            hasWizard: hasWizard,
            observation: observation,
            promoId: productDetails.promoId,
            promoPriceType: productDetails.promoPriceType,
            hasPhoto: productDetails.hasPhoto,
            smallImageUrl: productDetails.images[0] ? productDetails.images[0]?.smallImageUrl : undefined,
            loyaltyCampaignId: productDetails.loyaltyCampaignId,
            redeemValue: productDetails.unitRewardPoints
        }

        let comboPrice = 0

        if (sizeProduct) {
            cartItem = {
                ...cartItem,
                ...ShoppingCartFactory.getSizeProduct(params, sizeProduct),
            }

            if (sizeProduct.isGroup) {
                cartItem.subItems = ShoppingCartFactory.getSubItems(params, sizeProduct)
            }
            else {
                cartItem.name = ShoppingCartFactory.getName(`${productDetails.name} - ${sizeProduct.name}`, redeemObs)
                cartItem.personalizedName = undefined
            }

            const answers = ShoppingCartFactory.getSizeAnswersFromCustomer(orderedAnswers)

            if (hasAnswersFromCustomer) {
                if (cartItem.subItems)
                    cartItem.subItems.push(...answers)
                else
                    cartItem.subItems = answers
            }
        }
        else if (hasAnswersFromCustomer) {
            cartItem = {
                ...cartItem,
                ...ShoppingCartFactory.getWizardProduct(params),
            }

            const answers = ShoppingCartFactory.getAnswersFromCustomer(
                orderedAnswers,
                isCombo,
                merchantSummary?.linkWizardAnswersToParentItem
            )

            cartItem.subItems = answers.subItems
            comboPrice = answers.comboPrice

            if (answers.subItemsOnParent && answers.subItemsOnParent.length > 0) {
                cartItem.subItems.push(...answers.subItemsOnParent)
            }
        }

        if (isRedeemItem)
            cartItem.totalPrice = 0

        if (comboPrice > 0)
            cartItem.totalPrice = comboPrice

        if (sizeProduct?.isGroup || isCombo || hasWizard) {
            for (let i = 0; i < quantity; i++) {
                handleAddItem(cartItem)
            }
        }
        else {
            handleAddItem(cartItem)
        }

        Overlay.dismiss()
    }

    return { addToCart }
}